import { useSearchParams } from "react-router-dom";
import { VIEW_TYPES } from "../../../../../config/registers/view/types";
import { getUniversitiesRecognition } from "../../../../../axios/api/services";
import useViewObjectControl from "../../../../../hooks/registers/view/useViewObjectControl";
import { BlockText, BoxSpg, CardSpg, GridSpg, Page404, TextSection, isArrayEmpty } from "@duosoftbg/nacid-components";
import { useTranslation } from "react-i18next";
import useViewPageObject from "../../../../../hooks/redux/useViewPageObject";
import PageWrapper from "../../../../components/common/layout/PageWrapper";
import { CardContent, CircularProgress } from "@mui/material";
import React, { useCallback } from "react";
import { Link } from "@mui/material";
import { ACADEMIC_RECOGNITION_TYPE } from "../../../../../config/registers/common";
import { ProcessEnvironments } from "@duosoftbg/nacid-frontoffice-components";

const UniversityRecognitionView = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const object = useViewPageObject();
  const data = object?.data;
  const viewType = VIEW_TYPES.ACADEMIC_RECOGNITION_UNIVERSITIES;
  const uniRecognitionId = searchParams.get("id");
  const serviceFn = useCallback(getUniversitiesRecognition(uniRecognitionId, true), [uniRecognitionId]);
  const { isLoading, notFound } = useViewObjectControl({ viewType, serviceFn, viewId: uniRecognitionId });
  const title = `${t("t.detail.academicRecognition.universities")} ${
    object?.data?.inputNumber ? object.data?.inputNumber : ""
  }`;

  if (notFound) {
    return <Page404 />;
  }

  return (
    <PageWrapper title={title}>
      <CardSpg my={4} style={{ overflow: "visible" }}>
        <CardContent style={{ padding: 24, position: "relative" }}>
          {isLoading && (
            <BoxSpg my={2} sx={{ display: "flex", boxShadow: 3 }} style={{ justifyContent: "center" }}>
              <CircularProgress size={20} />
            </BoxSpg>
          )}
          {data && (
            <>
              <TextSection label={"l.applicationData"} withDivider>
                <GridSpg container rowSpacing={3}>
                  <BlockText withGrid label={"l.numberAndDate"} text={data.inputNumber} />
                  <BlockText withGrid label={"l.diplomaOwner"} text={data.applicant} />
                  <BlockText withGrid label={"l.decisionType"} text={data.recognitionStatus?.name} />
                </GridSpg>
                <GridSpg container rowSpacing={3} mt={2}>
                  <BlockText withGrid label={"l.recognizedUniversity"} text={data.recognizedUniversity} />
                  <BlockText withGrid label={"l.recognizeRejectOrder"} text={data.protocolNumber} />
                </GridSpg>
                <GridSpg container rowSpacing={3} mt={2}>
                  <BlockText withGrid label={"l.detail.foreignUniversity"} text={data.university} />
                  <BlockText withGrid label={"l.detail.universityCountry"} text={data.universityCountry} />
                </GridSpg>
              </TextSection>
              <TextSection label={"l.diplomaData"} withDivider>
                <GridSpg container rowSpacing={3}>
                  <BlockText
                    withGrid
                    propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
                    label={"l.acquiredEduLevel"}
                    text={data.educationLevel}
                  />
                  <BlockText
                    withGrid
                    propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
                    label={"l.acquiredSpeciality"}
                    text={data.diplomaSpeciality}
                  />
                </GridSpg>
              </TextSection>
              <RecognizedSection data={data} />
              <RelatedEntriesSection data={data} />
            </>
          )}
        </CardContent>
      </CardSpg>
    </PageWrapper>
  );
};

const RelatedEntriesSection = ({ data }) => {
  const { t } = useTranslation();
  let previousRecognition = data?.previousRecognition;
  let nextRecognitions = data?.nextRecognitions;

  console.log(previousRecognition);
  console.log(nextRecognitions);

  if (!previousRecognition && isArrayEmpty(nextRecognitions)) {
    return null;
  }

  return (
    <TextSection label={"l.relatedEntries"} withDivider>
      {previousRecognition && (
        <>
          <div style={{ fontWeight: "bold", fontSize: 13, color: "#059" }}> {t("l.previousRecognition")}</div>
          <GridSpg container rowSpacing={3} my={2}>
            <Link
              target={"_blank"}
              style={{ fontSize: 14 }}
              href={`${ProcessEnvironments.Module.FrontOffice.Registers}/academic-recognitions/view?id=${previousRecognition.id}&type=${ACADEMIC_RECOGNITION_TYPE.UNIVERSITIES}`}
            >
              {`${previousRecognition?.inputNumber ? previousRecognition?.inputNumber + ", " : ""} ${
                previousRecognition?.applicant ? previousRecognition?.applicant + ", " : ""
              } ${previousRecognition?.protocolNumber ? previousRecognition?.protocolNumber : ""}`}
            </Link>
          </GridSpg>
        </>
      )}
      {nextRecognitions && nextRecognitions.length > 0 && (
        <>
          <div style={{ fontWeight: "bold", fontSize: 13, color: "#059", marginBottom: 8, marginTop: 12 }}>
            {t("l.nextRecognition")}
          </div>
          {nextRecognitions.map((nextRecognition) => (
            <GridSpg key={nextRecognition.id} container rowSpacing={3} mt={2}>
              <Link
                target={"_blank"}
                style={{ fontSize: 14 }}
                href={`${ProcessEnvironments.Module.FrontOffice.Registers}/academic-recognitions/view?id=${nextRecognition.id}&type=${ACADEMIC_RECOGNITION_TYPE.UNIVERSITIES}`}
              >
                {`${nextRecognition?.inputNumber ? nextRecognition?.inputNumber + ", " : ""} ${
                  nextRecognition?.applicant ? nextRecognition?.applicant + ", " : ""
                } ${nextRecognition?.protocolNumber ? nextRecognition?.protocolNumber : ""}`}
              </Link>
            </GridSpg>
          ))}
        </>
      )}
    </TextSection>
  );
};

const RecognizedSection = ({ data }) => {
  let recognizedEduLevel = data?.recognizedEduLevel;
  let recognizedSpeciality = data?.recognizedSpeciality;

  if (!recognizedEduLevel && !recognizedSpeciality) {
    return null;
  }

  return (
    <TextSection label={"l.recognized"} withDivider>
      <GridSpg container rowSpacing={3}>
        <BlockText
          withGrid
          propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
          label={"l.recognizedEduLevel"}
          text={recognizedEduLevel}
        />
        <BlockText
          withGrid
          propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
          label={"l.recognizedSpeciality"}
          text={recognizedSpeciality}
        />
      </GridSpg>
    </TextSection>
  );
};

export default UniversityRecognitionView;
